/* Web Push côté navigateur : enregistrement du service worker, permission,
 * (dé)abonnement PushManager et synchronisation avec manager-cms. */

import ApiNotificationCtrl from "../api/ApiNotificationCtrl";

// Clé publique VAPID (non secrète — la clé privée reste côté manager-cms).
export const VAPID_PUBLIC_KEY =
    "BNijhPH3iPmEqsw-i9MDYN0B-JGBmh05Ou__PK2_Sql1GD3t0w6Nw3h8LtO3dzB9fhgv1kYNG99GE4qwv8-JcfU";

export function pushSupported() {
    return (
        typeof navigator !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

async function registerSW() {
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    return reg;
}

/** État courant : {supported, subscribed, permission}. */
export async function getPushState() {
    if (!pushSupported()) return { supported: false, subscribed: false, permission: "unsupported" };
    let subscribed = false;
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        subscribed = !!sub;
    } catch (e) { /* ignore */ }
    return { supported: true, subscribed, permission: Notification.permission };
}

function extractKeys(sub) {
    const json = sub.toJSON();
    return { endpoint: sub.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

/** Active les notifications navigateur pour l'admin. Lève "denied"/"unsupported" si refus. */
export async function enablePush(admin) {
    if (!pushSupported()) throw new Error("unsupported");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("denied");

    const reg = await registerSW();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
    }
    const { endpoint, p256dh, auth } = extractKeys(sub);
    await ApiNotificationCtrl.pushSubscribe(admin, endpoint, p256dh, auth);
    return true;
}

/**
 * Affiche une notification navigateur locale, sans passer par le serveur.
 * Utilisé quand un onglet est ouvert : garantit une notification Chrome/Safari
 * même si l'abonnement push n'est pas actif. Sans effet si la permission n'a
 * pas été accordée.
 */
export async function showLocalNotification(title, body, url = "/admin/notifications") {
    if (!pushSupported() || Notification.permission !== "granted") return false;
    const options = {
        body: body || "",
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "promise-notification",
        // Reste affichée jusqu'à action de l'utilisateur : sur macOS, Chrome route
        // ces notifications vers son service « Alertes » au lieu d'une bannière
        // qui disparaît en quelques secondes.
        requireInteraction: true,
        data: { url },
    };
    try {
        // Via le service worker quand il est là : le clic rouvre/refocalise l'app.
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
            await reg.showNotification(title, options);
        } else {
            new Notification(title, options);
        }
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Vrai si une notification système portant ce tag a bien été matérialisée par le
 * navigateur. Sert à distinguer deux pannes qui se ressemblent côté utilisateur :
 * le push n'arrive pas (transport), ou il arrive mais l'OS refuse de l'afficher
 * (macOS : Réglages Système › Notifications › Chrome non autorisé).
 */
export async function notificationDelivered(tag, timeoutMs = 2500) {
    if (!pushSupported()) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        try {
            const list = await reg.getNotifications({ tag });
            if (list.length > 0) return true;
        } catch (e) { /* ignore */ }
        await new Promise(r => setTimeout(r, 250));
    }
    return false;
}

/** Désactive les notifications navigateur pour l'admin (désabonne + informe le backend). */
export async function disablePush(admin) {
    if (!pushSupported()) return;
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
            const endpoint = sub.endpoint;
            try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
            await ApiNotificationCtrl.pushUnsubscribe(admin, endpoint);
        }
    } catch (e) { /* ignore */ }
}
